import React, { useState, useCallback, useEffect, useRef } from 'react';
// ReactDOM import is no longer needed here as App.tsx handles the overlay if portaled
import { useTick, useApplication, useExtend } from '@pixi/react'; 
import { TextStyle, type TextStyleAlign, FederatedPointerEvent, Text as PixiTextClass, Container as PixiContainerClass, Graphics as PixiGraphicsClass } from 'pixi.js'; 
import BirdComponent, { type BirdBounds } from './BirdComponent'; 
import PipeManagerComponent from './PipeManagerComponent'; 
import ScoreDisplayComponent from './ScoreDisplayComponent';
import BackgroundComponent from './BackgroundComponent'; 
import HighScoreDisplayComponent from './HighScoreDisplayComponent'; 
import { API_BASE_URL } from '../constants'; // ADDED IMPORT

// PlayerNameInputOverlay is no longer directly rendered or imported for rendering by GameComponent

export type GameState = 'menu' | 'playing' | 'gameOver' | 'showingSubmitStatus'; 
export type Difficulty = 'easy' | 'normal' | 'hard'; 

interface GameComponentProps {
  width: number; 
  height: number; 
  onGameOverShowOverlay: (score: number) => void; 
  submitName: string | null; 
  submitScoreTrigger: number; 
}

// Text Styles
const SHARED_TEXT_STYLE_PROPS = {
  fontFamily: '"Press Start 2P", Courier, monospace',
  fill: 0xffffff,
  stroke: { color: 0x000000, width: 4, join: 'round' as const },
  align: 'center' as TextStyleAlign,
};

const MENU_TEXT_STYLE = new TextStyle({ ...SHARED_TEXT_STYLE_PROPS, fontSize: 28 });
const SELECTED_DIFFICULTY_TEXT_STYLE = new TextStyle({ ...SHARED_TEXT_STYLE_PROPS, fontSize: 28, fill: 0xFFFF00 });
const DIFFICULTY_LABEL_STYLE = new TextStyle({ ...SHARED_TEXT_STYLE_PROPS, fontSize: 20 });
const GAME_OVER_MAIN_TEXT_STYLE = new TextStyle({ ...SHARED_TEXT_STYLE_PROPS, fontSize: 40 });
const GAME_OVER_SUB_TEXT_STYLE = new TextStyle({ ...SHARED_TEXT_STYLE_PROPS, fontSize: 22, lineHeight: 30 });
const SUBMISSION_STATUS_STYLE = new TextStyle({ ...SHARED_TEXT_STYLE_PROPS, fontSize: 24, wordWrap: true, wordWrapWidth: 300 });


// Difficulty Settings
interface BirdDifficultyParams { gravity: number; flapStrength: number; maxYVelocity: number; }
interface PipeDifficultyParams { gapVertical: number; speed: number; spawnIntervalFrames: number; }
interface DifficultySettings { bird: BirdDifficultyParams; pipes: PipeDifficultyParams; }

const DIFFICULTY_CONFIG: Record<Difficulty, DifficultySettings> = {
  easy: { bird: { gravity: 0.45, flapStrength: -9, maxYVelocity: 13 }, pipes: { gapVertical: 210, speed: 2.3, spawnIntervalFrames: 125 } },
  normal: { bird: { gravity: 0.6, flapStrength: -10, maxYVelocity: 14 }, pipes: { gapVertical: 190, speed: 2.8, spawnIntervalFrames: 110 } },
  hard: { bird: { gravity: 0.75, flapStrength: -11, maxYVelocity: 16 }, pipes: { gapVertical: 170, speed: 3.3, spawnIntervalFrames: 95 } },
};

const GameComponent: React.FC<GameComponentProps> = ({ 
  width, 
  height, 
  onGameOverShowOverlay, 
  submitName, 
  submitScoreTrigger 
}) => {
  useExtend({ Text: PixiTextClass, Container: PixiContainerClass, Graphics: PixiGraphicsClass });

  const appState = useApplication(); 
  const app = appState?.app; 

  const [gameState, setGameState] = useState<GameState>('menu'); 
  const [score, setScore] = useState<number>(0); 
  const [flapTriggerCount, setFlapTriggerCount] = useState<number>(0);
  const [currentBirdBounds, setCurrentBirdBounds] = useState<BirdBounds | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty>('normal'); 
  
  const [currentBirdParams, setCurrentBirdParams] = useState<BirdDifficultyParams>(DIFFICULTY_CONFIG.normal.bird);
  const [currentPipeParams, setCurrentPipeParams] = useState<PipeDifficultyParams>(DIFFICULTY_CONFIG.normal.pipes);
  const [submissionStatusMessage, setSubmissionStatusMessage] = useState<string | null>(null); 
  const statusMessageTimerRef = useRef<number | null>(null);

  const birdInitialX = width / 4;
  const birdInitialY = height / 2; 

  const handleBirdPositionUpdate = useCallback((bounds: BirdBounds) => setCurrentBirdBounds(bounds), []);
  const handleScorePoint = useCallback(() => setScore(prevScore => prevScore + 1), []);
  
  const handleCollision = useCallback(() => { 
    if (gameState === 'playing') { 
      requestAnimationFrame(() => {
        setGameState('gameOver');
      });
      console.log("Collision registered. Game Over. Signalling App to show overlay.");
    } 
  }, [gameState]);

  const handleStageInteraction = useCallback(() => {
    if (gameState === 'showingSubmitStatus') return; 

    console.log(`[GameComponent DEBUG] handleStageInteraction Fired! Current gameState: ${gameState}, difficulty: ${difficulty}`); 
    if (gameState === 'playing') {
        setFlapTriggerCount(c => c + 1);
    } else if (gameState === 'menu' || gameState === 'gameOver') { 
        console.log(`[GameComponent DEBUG] handleStageInteraction: 'menu' or 'gameOver' branch taken. Calling setGameState('playing').`); 
        console.log(`Starting game (diff: ${difficulty}, state: ${gameState})`); 
        setGameState('playing'); 
    }
  }, [gameState, difficulty]); 

  const handleDifficultySelect = useCallback((event: FederatedPointerEvent, selDiff: Difficulty) => {
    if (gameState === 'showingSubmitStatus') return;
    event.stopPropagation(); setDifficulty(selDiff);
    setCurrentBirdParams(DIFFICULTY_CONFIG[selDiff].bird); setCurrentPipeParams(DIFFICULTY_CONFIG[selDiff].pipes);
    console.log(`Difficulty: ${selDiff}`);
  }, [gameState]); 

  const handleReturnToMenu = useCallback((event: FederatedPointerEvent) => { 
    if (gameState === 'showingSubmitStatus') return;
    event.stopPropagation(); 
    setGameState('menu'); 
    console.log("To Menu"); 
  }, [gameState]); 

  const handleScoreSubmitAPI = useCallback(async (nameForSubmission: string) => { 
    console.log(`[GameComponent handleScoreSubmitAPI] Entry. nameForSubmission arg: '${nameForSubmission}', local score: ${score}, difficulty: ${difficulty}`);
    const submissionData = { playerName: nameForSubmission, score };
    console.log(`[GameComponent handleScoreSubmitAPI] Preparing to send to backend: ${JSON.stringify(submissionData)}`);
    
    setGameState('showingSubmitStatus'); 
    let statusMsg = '';

    try {
      const response = await fetch(`${API_BASE_URL}/api/scores`, { // Used API_BASE_URL
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(submissionData)
      });
      let result; try { result = await response.json(); } catch (e) { result = { error: `Parse fail. Status: ${response.status}`, details: await response.text() };}
      if (response.ok) { 
        console.log('Submit OK:', result); 
        statusMsg = `OK! ${result.player_name}: ${result.score}`;
      } else { 
        console.error('Submit Fail:', result); 
        statusMsg = `FAIL: ${result.error || result.details}`;
      }
    } catch (err) { 
      console.error('Submit Fetch Err:', err); 
      statusMsg = `ERROR: ${(err as Error).message}`;
    }

    setSubmissionStatusMessage(statusMsg);
    if (statusMessageTimerRef.current) {
      clearTimeout(statusMessageTimerRef.current);
    }
    statusMessageTimerRef.current = window.setTimeout(() => {
      setSubmissionStatusMessage(null);
      setGameState('menu'); 
    }, 3000); 

  }, [score, difficulty]); 

  // Effect for game start and parameter setup based on difficulty
  useEffect(() => {
    if (gameState === 'playing') {
      console.log(`[GameComponent Effect] gameState changed to 'playing' (Difficulty: ${difficulty}). Resetting score and game elements.`);
      setScore(0); 
      setFlapTriggerCount(c => c + 1); 
      setCurrentBirdBounds(null); 
      setSubmissionStatusMessage(null); 
      setCurrentBirdParams(DIFFICULTY_CONFIG[difficulty].bird); 
      setCurrentPipeParams(DIFFICULTY_CONFIG[difficulty].pipes);
    } else if (gameState === 'menu') {
      console.log("[GameComponent Effect] gameState changed to 'menu'.");
      setSubmissionStatusMessage(null); 
    }
  }, [gameState, difficulty]); 

  // Effect for handling game over overlay
  useEffect(() => {
    if (gameState === 'gameOver') {
      console.log(`[GameComponent Effect] gameState changed to 'gameOver'. Current score: ${score}. Calling onGameOverShowOverlay.`);
      onGameOverShowOverlay(score);
    }
  }, [gameState, score, onGameOverShowOverlay]); 

  // Effect for stage interaction listener
  useEffect(() => {
    if (app && app.stage && app.renderer) { 
      const stage = app.stage; 
      console.log(`[GameComponent StageListenerEffect DEBUG] Running. app ready. Current gameState: ${gameState}, stage.interactive: ${stage.interactive}`); 
      stage.interactive = true; 
      stage.hitArea = app.screen; 
      
      stage.off('pointerdown', handleStageInteraction); 
      if (gameState !== 'showingSubmitStatus') { 
        stage.on('pointerdown', handleStageInteraction); 
      }
      console.log(`[GameComponent StageListenerEffect DEBUG] 'pointerdown' listener attached (or intentionally not if showing status). Handler created with gameState: ${gameState}, difficulty: ${difficulty}`); 
      
      return () => { 
        if (stage) { 
          console.log(`[GameComponent StageListenerEffect DEBUG] Cleaning up 'pointerdown' listener. Listener was for gameState: ${gameState}, difficulty: ${difficulty}`); 
          stage.off('pointerdown', handleStageInteraction); 
        }
      };
    } else {
      console.log(`[GameComponent StageListenerEffect DEBUG] Skipping listener setup: app: ${!!app}, stage: ${!!(app && app.stage)}, renderer: ${!!(app && app.renderer)}`); 
    }
  }, [app, app?.renderer, gameState, handleStageInteraction]); 

  // Effect to handle score submission when triggered by App.tsx
  const prevSubmitScoreTriggerRef = useRef(submitScoreTrigger);
  useEffect(() => {
    if (submitScoreTrigger > prevSubmitScoreTriggerRef.current && submitName) {
      console.log(`[GameComponent SUBMIT EFFECT] Triggered. submitName from props: '${submitName}', local score: ${score}. Calling handleScoreSubmitAPI.`);
      handleScoreSubmitAPI(submitName);
    }
    prevSubmitScoreTriggerRef.current = submitScoreTrigger;
  }, [submitScoreTrigger, submitName, score, handleScoreSubmitAPI]);

  useTick(() => { 
    if (gameState === 'playing' && app && currentBirdBounds) {
      const birdTop = currentBirdBounds.y;
      const birdBottom = currentBirdBounds.y + currentBirdBounds.height;
      let collisionReason = "";

      if (birdBottom >= height) { 
        collisionReason = "ground";
      } else if (birdTop <= 0) { 
        collisionReason = "ceiling";
      }

      if (collisionReason) {
        console.log(`[GameComponent Boundary Collision DEBUG] Reason: ${collisionReason}, BirdTop: ${birdTop.toFixed(2)}, BirdBottom: ${birdBottom.toFixed(2)}, ScreenHeight: ${height}`);
        handleCollision(); 
      }
    }
  });

  const currentAppWidth = (app && app.renderer) ? app.renderer.width : width;
  const currentAppHeight = (app && app.renderer) ? app.renderer.height : height;
  
  // MODIFIED commonPositions calculation for menu alignment
  const birdCenterYForMenuLayout = birdInitialY; 
  const difficultyOptionVerticalSpacing = currentAppHeight * 0.06; 
  const difficultyLabelVerticalOffset = currentAppHeight * 0.07; 
  const scoresVerticalOffsetFromSelectors = currentAppHeight * 0.12;

  const commonPositions = { 
    tapToStart: currentAppHeight * 0.3, 
    diffLabel: birdCenterYForMenuLayout - difficultyOptionVerticalSpacing - difficultyLabelVerticalOffset, 
    easy: birdCenterYForMenuLayout - difficultyOptionVerticalSpacing,
    normal: birdCenterYForMenuLayout, 
    hard: birdCenterYForMenuLayout + difficultyOptionVerticalSpacing,
    scoresY: birdCenterYForMenuLayout + difficultyOptionVerticalSpacing + scoresVerticalOffsetFromSelectors
  };
  const gameOverPositions = { mainText: currentAppHeight / 3, subText: currentAppHeight / 2 + 10, menuBtn: currentAppHeight / 2 + 75 };

  return (
    <> 
      <pixiContainer> 
        <BackgroundComponent screenWidth={currentAppWidth} screenHeight={currentAppHeight} gameState={gameState} />
        <BirdComponent x={birdInitialX} initialY={birdInitialY} flapTrigger={flapTriggerCount} gameState={gameState} gravity={currentBirdParams.gravity} flapStrength={currentBirdParams.flapStrength} maxYVelocity={currentBirdParams.maxYVelocity} onPositionUpdate={handleBirdPositionUpdate} />
        
        {(gameState === 'playing' || gameState === 'gameOver' || gameState === 'showingSubmitStatus') && app && ( 
          <PipeManagerComponent gameState={gameState} screenWidth={currentAppWidth} screenHeight={currentAppHeight} birdBounds={currentBirdBounds} pipeSpeed={currentPipeParams.speed} pipeGapVertical={currentPipeParams.gapVertical} spawnIntervalFrames={currentPipeParams.spawnIntervalFrames} onScorePoint={handleScorePoint} onCollision={handleCollision} />
        )}
        <ScoreDisplayComponent score={score} x={currentAppWidth / 2} y={currentAppHeight * 0.1} textStyle={{fontSize: (gameState === 'gameOver' || gameState === 'showingSubmitStatus') ? 28 : 36}} />

        {gameState === 'menu' && (
          <>
            <pixiText text="Tap to Start" x={currentAppWidth / 2} y={commonPositions.tapToStart} style={MENU_TEXT_STYLE} anchor={{ x: 0.5, y: 0.5 }} zIndex={100} />
            <pixiText text="Select Difficulty:" x={currentAppWidth / 2} y={commonPositions.diffLabel} style={DIFFICULTY_LABEL_STYLE} anchor={{ x: 0.5, y: 0.5 }} zIndex={100} />
            <pixiText text="Easy" x={currentAppWidth / 2} y={commonPositions.easy} style={difficulty === 'easy' ? SELECTED_DIFFICULTY_TEXT_STYLE : MENU_TEXT_STYLE} anchor={{ x: 0.5, y: 0.5 }} eventMode='static' onPointerDown={(e: FederatedPointerEvent) => handleDifficultySelect(e, 'easy')} zIndex={100} />
            <pixiText text="Normal" x={currentAppWidth / 2} y={commonPositions.normal} style={difficulty === 'normal' ? SELECTED_DIFFICULTY_TEXT_STYLE : MENU_TEXT_STYLE} anchor={{ x: 0.5, y: 0.5 }} eventMode='static' onPointerDown={(e: FederatedPointerEvent) => handleDifficultySelect(e, 'normal')} zIndex={100} />
            <pixiText text="Hard" x={currentAppWidth / 2} y={commonPositions.hard} style={difficulty === 'hard' ? SELECTED_DIFFICULTY_TEXT_STYLE : MENU_TEXT_STYLE} anchor={{ x: 0.5, y: 0.5 }} eventMode='static' onPointerDown={(e: FederatedPointerEvent) => handleDifficultySelect(e, 'hard')} zIndex={100} />
            <HighScoreDisplayComponent x={currentAppWidth / 2 - 150} y={commonPositions.scoresY} /> 
          </>
        )}

        {(gameState === 'gameOver' || gameState === 'showingSubmitStatus') && !submissionStatusMessage && ( 
          <>
            <pixiText text="GAME OVER" x={currentAppWidth / 2} y={gameOverPositions.mainText} style={GAME_OVER_MAIN_TEXT_STYLE} anchor={{ x: 0.5, y: 0.5 }} zIndex={100} />
            <pixiText text={`Final Score: ${score}\n\nTap to Restart`} x={currentAppWidth / 2} y={gameOverPositions.subText} style={GAME_OVER_SUB_TEXT_STYLE} anchor={{ x: 0.5, y: 0.5 }} zIndex={100} />
            <pixiText text="Main Menu" x={currentAppWidth / 2} y={gameOverPositions.menuBtn} style={MENU_TEXT_STYLE} anchor={{ x: 0.5, y: 0.5 }} eventMode='static' onPointerDown={handleReturnToMenu} zIndex={100} />
          </>
        )}

        {submissionStatusMessage && (
          <pixiText
            text={submissionStatusMessage}
            x={currentAppWidth / 2}
            y={currentAppHeight / 2} 
            style={SUBMISSION_STATUS_STYLE} 
            anchor={{ x: 0.5, y: 0.5 }}
            zIndex={200} 
          />
        )}
      </pixiContainer>
      {/* PlayerNameInputOverlay is no longer rendered here. App.tsx handles it. */}
    </>
  );
};

export default GameComponent;
// Rewrite Count: 80
